import { Component } from '@theme/component';

/**
 * Product sticky bar.
 *
 * Two jobs, both of them mirroring:
 *
 * 1. Scroll spy. Underlines the nav item whose target section the reader is
 *    currently in. Targets are read out of the rendered DOM, so adding a nav
 *    item in the editor needs no JS change.
 *
 * 2. Plan state. Copies size / term / payment from <plan-selector-component>
 *    onto its own root so the pre-rendered panel for that combination is
 *    revealed. It never resolves variant or plan ids itself and never formats
 *    money — the selector owns that, and the panels are already rendered.
 *
 * The button behaves one of two ways, set by the section's checkout mode.
 * In external mode it is an anchor whose href is promoted from the plan
 * selector to the gateway once a plan has actually been chosen. In Shopify
 * mode it delegates to the selector's own `addToCart()`, if the selector on
 * the page still has one.
 *
 * @typedef {object} StickyBarRefs
 * @property {HTMLElement} nav
 * @extends Component<StickyBarRefs>
 */
class ProductStickyBarComponent extends Component {
  /** @type {MutationObserver | undefined} */
  #customWatcher;

  requiredRefs = ['nav'];

  /** @type {IntersectionObserver | null} */
  #spy = null;
  /** @type {IntersectionObserver | null} */
  #dockWatcher = null;
  /** @type {ResizeObserver | null} */
  #resize = null;
  /** @type {MediaQueryList | null} */
  #mobile = null;
  /** @type {MutationObserver | null} */
  #mirrorWatcher = null;
  /** @type {Set<Element>} */
  #visible = new Set();

  connectedCallback() {
    super.connectedCallback();

    this.#publishHeight();
    this.#resize = new ResizeObserver(() => this.#publishHeight());
    this.#resize.observe(this);
    if (this.refs.dock) this.#resize.observe(this.refs.dock);

    this.#mobile = window.matchMedia('(max-width: 749px)');
    this.#mobile.addEventListener('change', this.#onBreakpoint);

    document.addEventListener('plan-selector:change', this.#onPlanChange);

    // The configuration changes without any variant or plan event firing, so the bar
    // watches what the Custom Home panel publishes as well.
    const customPanel = document.querySelector('custom-home-panel');
    if (customPanel) {
      this.#customWatcher = new MutationObserver(() => this.#syncCustomCta());
      this.#customWatcher.observe(customPanel, {
        attributes: true,
        attributeFilter: ['data-sb-price', 'data-sb-url', 'data-sb-meta'],
      });
    }

    // The selector renders after this section, so wait for the document before
    // taking the initial reading. Until then the Liquid defaults stand.
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', this.#init, { once: true });
    } else {
      this.#init();
    }
  }

  disconnectedCallback() {
    this.#mirrorWatcher?.disconnect();
    this.#mirrorWatcher = null;
    super.disconnectedCallback?.();
    document.removeEventListener('plan-selector:change', this.#onPlanChange);
    this.#customWatcher?.disconnect();
    this.#mobile?.removeEventListener('change', this.#onBreakpoint);
    this.#spy?.disconnect();
    this.#dockWatcher?.disconnect();
    this.#resize?.disconnect();
    document.documentElement.classList.remove('sticky-bar-docked');
  }

  #init = () => {
    // Set up in #init, not in connectedCallback: this section is the first in
    // the page's order, so at connect time the rail it mirrors has not been
    // parsed yet and document.querySelector would come back null -- the watcher
    // would never be attached and the bar would sit on its Liquid defaults for
    // the life of the page.
    this.#watchMirror();
    this.#syncFromSelector();
    this.#syncCtaState();
    this.#startSpy();
    this.#onBreakpoint();
  };

  /* ------------------------------------------------------------------ */
  /* Mobile dock                                                         */
  /* ------------------------------------------------------------------ */

  #onBreakpoint = () => {
    const isMobile = this.#mobile?.matches ?? false;
    document.documentElement.classList.toggle('sticky-bar-docked', isMobile);

    this.#dockWatcher?.disconnect();
    this.#dockWatcher = null;

    if (!isMobile) {
      this.removeAttribute('data-dock-hidden');
      return;
    }

    this.#watchSelectorCta();
    this.#publishHeight();
  };

  /**
   * Withdraws the dock while the selector's own Add to Cart is on screen.
   * Falls back to always-on if that button can't be found, which is the safer
   * failure — a redundant button beats no button.
   */
  #watchSelectorCta() {
    // The selector renders one CTA per plan and hides all but the current one,
    // so watch them all. Hidden panels never intersect, which means the set
    // only ever holds the visible button.
    // The mirrored rail owns its own submit buttons, and they carry the same
    // class, so the dock still withdraws when the real CTA is on screen.
    const root = this.#mirror ?? this.#selector;
    const targets = root?.querySelectorAll('[ref="submit"], .plan-selector__submit');
    if (!targets?.length) return;

    /** @type {Set<Element>} */
    const onScreen = new Set();

    this.#dockWatcher = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) onScreen.add(entry.target);
          else onScreen.delete(entry.target);
        }
        this.toggleAttribute('data-dock-hidden', onScreen.size > 0);
      },
      { threshold: 0 }
    );

    for (const target of targets) this.#dockWatcher.observe(target);
  }

  /* ------------------------------------------------------------------ */
  /* Plan state                                                          */
  /* ------------------------------------------------------------------ */

  get #selector() {
    return document.querySelector('plan-selector-component');
  }

  /** Starts mirroring, once the mirrored rail exists. */
  #watchMirror() {
    const mirror = this.#mirror;
    if (!mirror) return;

    this.#mirrorWatcher = new MutationObserver(() => this.#syncMirror());
    this.#mirrorWatcher.observe(mirror, {
      attributes: true,
      attributeFilter: ['data-sb-title', 'data-sb-meta', 'data-sb-price', 'data-sb-cta', 'data-sb-url'],
    });

    // Twice, and the second one matters. The mirrored rail publishes from its
    // own connectedCallback, which for a classic inline script runs during
    // parsing -- before the page has laid out. The figures it reads are chosen
    // by offsetParent, so anything it resolved too early is corrected on its
    // next sync; taking a second reading after a frame means the bar does not
    // sit on its Liquid defaults until the reader touches something.
    this.#syncMirror();
    requestAnimationFrame(() => this.#syncMirror());
  }

  /** The element this bar mirrors, when it is configured to mirror one. */
  get #mirror() {
    const selector = this.dataset.mirrorSelector;
    return selector ? document.querySelector(selector) : null;
  }

  /**
   * Fills the mirror panel from whatever the mirrored rail publishes. The rail
   * owns the wording and the destination; the bar owns only the shape, so a
   * product whose options this section cannot model still gets a live price and
   * a working button.
   */
  #syncMirror() {
    const source = this.#mirror;
    const cta = this.querySelector('[data-sb-mirror-cta]');
    if (!source || !(cta instanceof HTMLAnchorElement)) return;

    const price = source.dataset.sbPrice || '';
    const url = source.dataset.sbUrl || '';
    // Both, or the button would offer a checkout it has no address for.
    const ready = Boolean(price) && Boolean(url);

    cta.classList.toggle('is-ready', ready);
    cta.classList.toggle('is-choosing', !ready);

    const write = (selector, value) => {
      const node = this.querySelector(selector);
      if (node) node.textContent = value;
    };
    write('[data-sb-mirror-title]', source.dataset.sbTitle || '');
    write('[data-sb-mirror-meta]', source.dataset.sbMeta || '');
    write('[data-sb-mirror-price]', price);
    // Only overwrite the Liquid default when the rail actually supplies a label,
    // and never leave a stale one behind: a blank publish means the rail has
    // nothing to offer, and the button falls back to its choose state anyway.
    const label = this.querySelector('[data-sb-mirror-label]');
    if (label) {
      if (label.dataset.defaultText == null) label.dataset.defaultText = label.textContent.trim();
      label.textContent = source.dataset.sbCta || label.dataset.defaultText;
    }

    cta.href = ready ? url : cta.dataset.chooseHref || cta.href;
  }

  /** @param {Event} event */
  #onPlanChange = (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail;
    if (!detail) return;

    // Only a real interaction reaches here — the selector doesn't fire this on
    // load — so the first one is what promotes the button from "choose" to
    // "buy". Without it the bar would offer to sell a plan the page picked.
    this.dataset.userSelected = 'true';
    this.#apply(detail.size, detail.term, detail.payment);
    this.#syncCtaState();
  };

  get #userSelected() {
    return this.dataset.userSelected === 'true';
  }

  get #external() {
    return this.dataset.checkoutMode !== 'shopify';
  }

  /**
   * Mirrors the flag onto the buttons so both states are styleable by class,
   * and promotes the href from the plan anchor to the gateway.
   *
   * Liquid renders the anchor pointing at the plan selector, so a reader
   * without JS is sent to choose rather than to checkout with whatever the
   * page defaulted to. Once a plan has genuinely been picked, the real
   * destination moves out of data-checkout-url and into href.
   */
  #syncCtaState() {
    const chosen = this.#userSelected;

    for (const cta of this.querySelectorAll('.sticky-bar__cta')) {
      // The off-catalogue panel is not driven by "has a variant been chosen" -- there is
      // no variant. It is driven by whether the Custom Home panel has published a
      // configuration that can actually be bought, so it is handled on its own.
      if (cta.hasAttribute('data-sb-custom-cta')) continue;

      // Same reasoning for a mirrored rail: "has a variant been chosen" is not
      // the question there either. The rail publishes a price and a destination
      // or it does not, and #syncMirror is what reads that. Without this the
      // loop below stamped is-choosing back over the mirror CTA immediately
      // after #syncMirror had set it ready, and the bar sat on "Choose a plan"
      // with a live price already written into the hidden half of the button.
      if (cta.hasAttribute('data-sb-mirror-cta')) continue;

      cta.classList.toggle('is-ready', chosen);
      cta.classList.toggle('is-choosing', !chosen);

      if (!(cta instanceof HTMLAnchorElement)) continue;
      const { checkoutUrl, chooseHref } = cta.dataset;
      if (!checkoutUrl) continue;
      cta.href = chosen ? checkoutUrl : chooseHref || cta.href;
    }

    this.#syncCustomCta();
    this.#syncMirror();
  }

  /**
   * The Custom Home panel publishes three things on itself once its configuration is
   * buyable: the figure, a line describing it, and where the button should go. With them
   * this reads exactly as a fixed size does -- price, a middot, and Start. Without them
   * it stays the enquiry it has always been, which is the right answer before anything
   * has been configured.
   */
  #syncCustomCta() {
    const cta = this.querySelector('[data-sb-custom-cta]');
    if (!(cta instanceof HTMLAnchorElement)) return;

    const panel = document.querySelector('custom-home-panel');
    const price = panel?.dataset.sbPrice || '';
    const url = panel?.dataset.sbUrl || '';
    const ready = this.dataset.size === 'custom-size' && !!price && !!url;

    cta.classList.toggle('is-ready', ready);
    cta.classList.toggle('is-choosing', !ready);

    const priceNode = cta.querySelector('[data-sb-custom-price]');
    if (priceNode) {
      /* Always the per-month suffix. The panel publishes a per-month figure for every
         plan, including upfront -- the basis for that one is stated in the meta line
         above, which the panel also supplies. The bar still owns the wording. */
      // A yearly figure when the panel says so (Annual paid upfront), else per month.
      const suffix = panel?.dataset.sbBasis === 'year'
        ? ` ${this.dataset.upfrontSuffix || 'for the year'}`
        : this.dataset.perMonth || '';
      priceNode.textContent = ready ? `${price}${suffix}` : '';
    }

    const meta = this.querySelector('[data-sb-custom-meta]');
    if (meta) {
      if (meta.dataset.defaultText == null) meta.dataset.defaultText = meta.textContent.trim();
      meta.textContent = ready && panel?.dataset.sbMeta ? panel.dataset.sbMeta : meta.dataset.defaultText;
    }

    if (ready) {
      cta.href = url;
      // The enquiry opens in a new tab; a checkout should not.
      cta.removeAttribute('target');
      cta.removeAttribute('rel');
    } else {
      cta.href = cta.dataset.chooseHref || cta.href;
      if ((cta.dataset.chooseHref || '').includes('wa.me')) {
        cta.setAttribute('target', '_blank');
        cta.setAttribute('rel', 'noopener');
      }
    }
  }

  /** Reads the selector's current state on load, including a ?variant deep link. */
  #syncFromSelector() {
    const selector = this.#selector;
    if (!(selector instanceof HTMLElement)) return;
    this.#apply(selector.dataset.size, selector.dataset.term, selector.dataset.payment);
  }

  /**
   * @param {string | undefined} size
   * @param {string | undefined} term
   * @param {string | undefined} payment
   */
  #apply(size, term, payment) {
    if (size) this.dataset.size = size;
    if (term) this.dataset.term = term;
    if (payment) this.dataset.payment = payment;
  }

  /* ------------------------------------------------------------------ */
  /* Checkout                                                            */
  /* ------------------------------------------------------------------ */

  /**
   * External mode. Before a plan is chosen the anchor is pointing at the plan
   * selector anyway, so letting the default happen would work — but scrolling
   * smoothly reads better than a hash jump, and it keeps the behaviour
   * identical to the Shopify path.
   *
   * @param {Event} event
   */
  checkout(event) {
    if (this.#userSelected) return;
    event.preventDefault();
    this.#scrollTo(this.dataset.planAnchor || '');
  }

  /* ------------------------------------------------------------------ */
  /* Add to cart                                                         */
  /* ------------------------------------------------------------------ */

  /** @param {Event} event */
  async addToCart(event) {
    event.preventDefault();

    // Nothing chosen yet: send them to the picker rather than committing them
    // to the default. With skip-the-cart on there is no screen after this one.
    if (!this.#userSelected) {
      this.#scrollTo(this.dataset.planAnchor || '');
      return;
    }

    const selector = this.#selector;

    // No selector on the page, not upgraded yet, or a selector built for
    // external checkout that has no cart at all — send the reader to the plan
    // section rather than failing silently.
    if (!selector || typeof selector.addToCart !== 'function') {
      console.warn('[sticky-bar] Checkout mode is Shopify but the plan selector has no addToCart().');
      this.#scrollTo(this.dataset.planAnchor || '');
      return;
    }

    await selector.addToCart();
  }

  /* ------------------------------------------------------------------ */
  /* Navigation                                                          */
  /* ------------------------------------------------------------------ */

  /** @param {Event} event */
  goToSection(event) {
    const link = /** @type {Element} */ (event.target)?.closest?.('[data-nav-target]');
    if (!(link instanceof HTMLElement)) return;

    const id = link.dataset.navTarget;
    if (!id || !document.getElementById(id)) return;

    event.preventDefault();
    this.#scrollTo(id);

    // Reflect the destination straight away; the spy will confirm it.
    this.#markActive(id);
    history.replaceState({}, '', `#${id}`);
  }

  /** @param {string} id */
  #scrollTo(id) {
    const target = id && document.getElementById(id);
    if (!target) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
  }

  #startSpy() {
    const links = [...this.refs.nav.querySelectorAll('[data-nav-target]')];
    /** @type {HTMLElement[]} */
    const targets = [];

    for (const link of links) {
      if (!(link instanceof HTMLElement)) continue;
      const target = document.getElementById(link.dataset.navTarget || '');
      if (target) targets.push(target);
    }

    if (!targets.length) return;

    // Ignore the strip the bar itself occupies, and the bottom 55% of the
    // viewport, so the active item tracks what is under the bar rather than
    // whatever happens to be entering from below.
    const top = Math.round(this.getBoundingClientRect().height + this.#headerHeight());

    this.#spy = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.#visible.add(entry.target);
          else this.#visible.delete(entry.target);
        }

        // Several sections can straddle the band; the last one in document
        // order is the one the reader has scrolled into.
        const current = targets.filter((target) => this.#visible.has(target)).pop();
        if (current) this.#markActive(current.id);
      },
      { rootMargin: `-${top}px 0px -55% 0px`, threshold: 0 }
    );

    for (const target of targets) {
      this.#spy.observe(target);
    }
  }

  /** @param {string} id */
  #markActive(id) {
    for (const link of this.refs.nav.querySelectorAll('[data-nav-target]')) {
      const active = link instanceof HTMLElement && link.dataset.navTarget === id;
      if (active) {
        link.setAttribute('aria-current', 'true');
        this.#keepInView(link);
      } else {
        link.removeAttribute('aria-current');
      }
    }
  }

  /** Keeps the active item visible in the horizontally scrolling mobile nav. */
  #keepInView(link) {
    const list = this.refs.nav;
    if (list.scrollWidth <= list.clientWidth) return;
    const item = link.getBoundingClientRect();
    const box = list.getBoundingClientRect();
    if (item.left >= box.left && item.right <= box.right) return;
    list.scrollTo({
      left: list.scrollLeft + (item.left - box.left) - 16,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  }

  #headerHeight() {
    const value = getComputedStyle(document.documentElement).getPropertyValue('--header-height');
    return parseFloat(value) || 0;
  }

  #publishHeight() {
    const root = document.documentElement.style;
    const barHeight = Math.round(this.getBoundingClientRect().height);
    root.setProperty('--sticky-bar-height', `${barHeight}px`);

    // One offset for every anchor on the page, applied as scroll-padding on
    // the scroller. Per-target scroll-margin only covered the nav items, so
    // anything else — the plan anchor especially — landed under the bar.
    root.setProperty('--sticky-bar-offset', `${barHeight + this.#headerHeight() + 24}px`);

    const dock = this.refs.dock;
    const docked = this.#mobile?.matches && dock;
    root.setProperty(
      '--sticky-bar-dock-height',
      docked ? `${Math.round(dock.getBoundingClientRect().height)}px` : '0px'
    );
  }
}

if (!customElements.get('product-sticky-bar-component')) {
  customElements.define('product-sticky-bar-component', ProductStickyBarComponent);
}